import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PriceData } from "~/domains/pricing/types/PriceData";

import {
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { isKITKITProduct } from "~/domains/product/types/ProductData";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import * as PriceUtils from "~/utils/PriceUtils";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";

export interface UseViewableLineItemDataProps {
  lineItem: LineItem;
  product: ProductDescriptor;
}

export interface ViewableLineItemPriceData {
  /** Formatted total price string */
  totalPriceFormatted: string;
  /** Whether product has a surcharge */
  hasSurcharge: boolean;
  /** Surcharge value if applicable */
  surchargeValue: PriceData | undefined;
  /** Tariff surcharge flag */
  tariffSurcharge: boolean;
  /** Price unit (quantity per price) */
  priceUnit: number;
  /** Zero price label to display */
  zeroPriceLabel: string;
  /** Whether the product has a price > 0 */
  hasPrice: boolean;
}

export interface ViewableLineItemFlags {
  /** Whether product is PMA type */
  isPMA: boolean;
  /** Whether product is a KIT product */
  isKIT: boolean;
  /** Whether quantity is visible */
  quantityVisible: boolean;
  /** Whether product is an Optime solution */
  isOptime: boolean;
  /** Whether line item is from bulk upload */
  isBulkUpload: boolean;
  /** Whether comment can be shown */
  canSeeComment: boolean;
  /** Whether web services are disabled */
  webservicesDisabled: boolean;
}

export interface ViewableLineItemFormatted {
  /** Formatted delivery date */
  deliveryDate: string;
  /** Formatted quantity */
  quantity: number;
}

export interface UseViewableLineItemDataResult {
  /** Price-related computed data */
  pricing: ViewableLineItemPriceData;
  /** Various boolean flags */
  flags: ViewableLineItemFlags;
  /** Formatted values for display */
  formatted: ViewableLineItemFormatted;
}

/**
 * Hook that computes derived data for a viewable (read-only) line item.
 * Used in checkout and order confirmation pages where editing is disabled.
 *
 * @param props - Line item and product descriptor
 * @returns Computed data for display
 */
export function useViewableLineItemData({
  lineItem,
  product,
}: UseViewableLineItemDataProps): UseViewableLineItemDataResult {
  const { t } = useTranslation();

  // Flags
  const isBulkUpload = lineItem.source === ("BULK" as PackageEntrySource);
  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;
  const isPMA = isProductPMA(product);
  const isKIT = isKITKITProduct(product.base);
  const canSeeComment = canUseCommentsForProduct(product.base.marketType);
  const webservicesDisabled = window.SCHA?.config?.disableAllWS ?? false;

  // Price calculations
  const pricing = useMemo((): ViewableLineItemPriceData => {
    const netPrice = lineItem.netPrice;
    const priceUnit = netPrice?.unit ?? 1;
    const zeroPriceLabel = isPMA || isKIT ? "-" : t("product.priceOnRequest");
    const tariffSurcharge = netPrice?.tariffSurcharge ?? false;

    if (isBulkUpload || !netPrice) {
      return {
        totalPriceFormatted: "...",
        hasSurcharge: false,
        surchargeValue: undefined,
        tariffSurcharge,
        priceUnit,
        zeroPriceLabel,
        hasPrice: false,
      };
    }

    const hasSurcharge = (netPrice.surcharge ?? 0) > 0 && netPrice.value > 0;
    const totalPriceVal = getTotalPriceForLineItem(lineItem, product);
    const surchargeValue = hasSurcharge ? getSurchargeValueFromTotalPrice(totalPriceVal) : undefined;
    const totalPriceFormatted = PriceUtils.text(totalPriceVal, "-");
    const hasPrice = netPrice.value > 0;

    return {
      totalPriceFormatted,
      hasSurcharge,
      surchargeValue,
      tariffSurcharge,
      priceUnit,
      zeroPriceLabel,
      hasPrice,
    };
  }, [lineItem, product, isBulkUpload, isPMA, isKIT, t]);

  // Formatted values
  const formatted = useMemo((): ViewableLineItemFormatted => {
    const deliveryDate = lineItem.deliveryDate ? formatDate(lineItem.deliveryDate, DateFormatType.DATE) : "–";

    return {
      deliveryDate,
      quantity: lineItem.quantity,
    };
  }, [lineItem.deliveryDate, lineItem.quantity]);

  // Flags
  const flags = useMemo((): ViewableLineItemFlags => {
    return {
      isPMA,
      isKIT,
      quantityVisible,
      isOptime,
      isBulkUpload,
      canSeeComment,
      webservicesDisabled,
    };
  }, [isPMA, isKIT, quantityVisible, isOptime, isBulkUpload, canSeeComment, webservicesDisabled]);

  return {
    pricing,
    flags,
    formatted,
  };
}
